import React, { useState, useRef, useEffect } from 'react';
import { Speaker } from '../types/profile.types';

interface MultiSelectSpeakersProps {
  selectedValues: string[]; // IDs de oradores seleccionados
  onChange: (values: string[]) => void;
  speakers: Speaker[];
  error?: string;
  disabled?: boolean;
}

const MultiSelectSpeakers: React.FC<MultiSelectSpeakersProps> = ({
  selectedValues,
  onChange,
  speakers,
  error,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Detectar modo oscuro
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark-theme'));
    };

    checkDarkMode();

    // Observar cambios en el atributo class del documentElement
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Obtener nombres de oradores para mostrar
  const getSpeakerName = (speakerId: string): string => {
    const speaker = speakers.find(s => s.id === speakerId);
    if (speaker) {
      const fullName = `${speaker.firstName} ${speaker.lastName}`.trim();
      return fullName || 'Sin nombre';
    }
    return 'Orador desconocido';
  };

  // Obtener oradores disponibles
  const availableSpeakers = speakers.filter(s => !selectedValues.includes(s.id));

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsFocused(false);
        setIsExpanded(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Detectar si es tablet
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsTablet(width >= 769 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  const handleContainerClick = () => {
    setIsOpen(true);
    setIsFocused(true);
    setIsExpanded(true);
  };

  const handleContainerFocus = () => {
    setIsFocused(true);
  };

  const handleContainerBlur = () => {
    if (!isOpen) {
      setIsFocused(false);
      setIsExpanded(false);
    }
  };

  const handleContainerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < availableSpeakers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : availableSpeakers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < availableSpeakers.length) {
          selectOption(availableSpeakers[highlightedIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  const selectOption = (speakerId: string) => {
    if (!selectedValues.includes(speakerId)) {
      onChange([...selectedValues, speakerId]);
    }
    setHighlightedIndex(-1);
  };

  const removeTag = (speakerIdToRemove: string) => {
    onChange(selectedValues.filter(id => id !== speakerIdToRemove));
    if (!isExpanded) {
      setIsFocused(false);
    }
  };

  const handleOptionClick = (speakerId: string) => {
    if (disabled) return;
    selectOption(speakerId);
  };

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100%' }} ref={containerRef}>
      {/* Contenedor de tags */}
      <div
        className="multi-select-speakers-container"
        style={{
          display: 'flex',
          flexWrap: isExpanded ? 'wrap' : 'nowrap',
          alignItems: isExpanded ? 'flex-start' : 'center',
          gap: '6px',
          height: isExpanded ? 'auto' : '38px',
          minHeight: '38px',
          width: '100%',
          maxWidth: '100%',
          padding: '8px 12px',
          border: error ? '1px solid #ef4444' : (isFocused ? '1px solid #CE2B15' : '1px solid #e5e7eb'),
          borderRadius: '6px',
          backgroundColor: isDarkMode ? undefined : (disabled ? '#f3f4f6' : '#ffffff'),
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 0.2s ease',
          boxShadow: isFocused && !error ? '0 0 0 2px rgba(206, 43, 21, 0.1)' : 'none',
          transform: isFocused && !error ? 'translateY(-1px)' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden'
        }}
        onClick={handleContainerClick}
        onFocus={handleContainerFocus}
        onBlur={handleContainerBlur}
        onKeyDown={handleContainerKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        {/* Tags seleccionados */}
        {selectedValues.map((speakerId, index) => {
          const maxVisibleChips = isTablet ? 1 : 2;
          const shouldShowChip = isExpanded || index < maxVisibleChips;

          if (!shouldShowChip) return null;

          const displayText = getSpeakerName(speakerId);
          const truncatedText = isExpanded ? displayText : (displayText.length > 14 ? displayText.substring(0, 14) + '...' : displayText);

          return (
            <div
              key={`${speakerId}-${index}`}
              className="multi-select-speakers-tag"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                padding: '2px 6px',
                backgroundColor: isDarkMode ? undefined : '#f1f5f9',
                border: '1px solid #d1d5db',
                borderRadius: '3px',
                fontSize: '11px',
                fontFamily: 'Montserrat, sans-serif',
                color: isDarkMode ? undefined : '#374151',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 'fit-content',
                maxWidth: isExpanded ? 'none' : '120px'
              }}
              title={displayText}
            >
              <span>{truncatedText}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(speakerId);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: isDarkMode ? undefined : '#6b7280',
                  fontSize: '10px',
                  lineHeight: 1,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '12px',
                  height: '12px'
                }}
              >
                ×
              </button>
            </div>
          );
        })}

        {/* Indicador de chips ocultas cuando no está expandido */}
        {!isExpanded && selectedValues.length > (isTablet ? 1 : 2) && (
          <div
            className="multi-select-speakers-tag-more"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '2px 6px',
              backgroundColor: isDarkMode ? undefined : '#e5e7eb',
              border: '1px solid #d1d5db',
              borderRadius: '3px',
              fontSize: '11px',
              fontFamily: 'Montserrat, sans-serif',
              color: isDarkMode ? undefined : '#6b7280',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
              setIsOpen(true);
              setIsFocused(true);
            }}
          >
            +{selectedValues.length - (isTablet ? 1 : 2)} más
          </div>
        )}

        {/* Placeholder cuando no hay tags */}
        {selectedValues.length === 0 && (
          <span
            className="multi-select-speakers-placeholder"
            style={{
              color: isDarkMode ? undefined : '#9ca3af',
              fontSize: '14px',
              fontFamily: 'Montserrat, sans-serif',
              fontStyle: 'italic'
            }}
          >
            {disabled ? '' : 'Seleccionar oradores...'}
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          className="multi-select-speakers-dropdown"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: isDarkMode ? undefined : '#ffffff',
            border: '1px solid #e5e7eb',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 9999,
            maxHeight: '250px',
            overflow: 'hidden'
          }}
        >
          {/* Lista de opciones */}
          <div style={{ maxHeight: '250px', overflow: 'auto' }}>
            {availableSpeakers.length > 0 ? (
              availableSpeakers.map((speaker, index) => {
                const fullName = `${speaker.firstName} ${speaker.lastName}`.trim() || 'Sin nombre';

                return (
                  <div
                    key={speaker.id}
                    className="multi-select-speakers-option"
                    onClick={() => handleOptionClick(speaker.id)}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: isDarkMode ? (index === highlightedIndex ? undefined : 'transparent') : (index === highlightedIndex ? '#f1f5f9' : 'transparent'),
                      color: isDarkMode ? undefined : '#374151',
                      fontFamily: 'Montserrat, sans-serif',
                      fontSize: '14px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px'
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{fullName}</span>
                    {speaker.organization && (
                      <span style={{ fontSize: '12px', color: isDarkMode ? undefined : '#6b7280' }}>{speaker.organization}</span>
                    )}
                  </div>
                );
              })
            ) : (
              <div
                className="multi-select-speakers-empty"
                style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: isDarkMode ? undefined : '#6b7280',
                  fontStyle: 'italic',
                  fontSize: '13px',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                Todos los oradores están seleccionados
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mensaje de error */}
      {error && (
        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default MultiSelectSpeakers;
